import * as React from 'react';
import { connect } from 'react-redux';
import snakeCase from 'lodash/snakeCase';

import ActionButton from 'components/buttons/action-button/ActionButton';
import ActionLink from 'components/buttons/action-link/ActionLink';
import FormField from 'components/form/form-field/FormField';
import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import { ModalFrameFooter, ModalFrameInner, ModalFrameMessage } from 'components/modal';

import BankDetailsSettings from 'modules/organizations/components/settings/BankDetailsSettings';
import BankDetailsSettingsForm from 'modules/organizations/components/settings/BankDetailsSettingsForm';
import InfluencerBusinessDetailsSettings from 'modules/organizations/components/settings/influencer/InfluencerBusinessDetailsSettings';
import InfluencerBusinessDetailsSettingsForm from 'modules/organizations/components/settings/influencer/InfluencerBusinessDetailsSettingsForm';

import { __t, applyTranslations } from 'utils/language';
import {
  getPaymentDetailsExplanationType,
  prepareSettingsPayload,
  shouldInfluencerFillBankDetails,
  shouldInfluencerFillBusinessDetails
} from 'utils/organization';
import { addProtocol } from 'utils/url';
import { isRequired } from 'utils/validators';
import { sanitizeGenericHtml } from 'utils/sanitize';

import { saveOrganizationSettings } from 'modules/organizations/reducers/organizationSettings';

import './shipping-step-settings-form.scss';

applyTranslations(require('./shipping-step-settings-form.i18n.json'));

import type {
  ActionButtonColor,
  OrganizationSettingsForm,
  OrganizationSettingsParams,
  OrganizationSettingsUpdateType
} from 'types';

type OwnProps = {
  settingsId: number,
  subcampaignId: number,
  organizationSettings: OrganizationSettingsForm,
  settingsGroup: 'businessDetails' | 'bankDetails',
  submitButtonColor?: ActionButtonColor,
  minHeight?: number,
  forceEditMode?: boolean,
  onCancel: () => void,
  onContinue?: (e: React.MouseEvent, shouldSkipStep?: true) => void,
  onSave?: () => void
};

type DispatchProps = {
  dispatchSaveOrganizationSettings: (id: number, data: OrganizationSettingsForm, params: OrganizationSettingsParams) => Promise<void>
}

type Props = OwnProps & DispatchProps;

type State = {
  disclaimer: boolean,
  editMode: boolean,
  areDetailsMissing: boolean,
  isModified: boolean,
  country: string | null,
  organizationDetails: OrganizationSettingsForm
};

const msgKey = 'shipping_step_settings_form';

class ShippingStepSettingsForm extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const areDetailsMissing = this.areDetailsMissing();

    this.state = {
      organizationDetails: this.getOrganizationDetailsState(this.props),
      country: null,
      editMode: this.props.forceEditMode || areDetailsMissing,
      disclaimer: !areDetailsMissing,
      isModified: false,
      areDetailsMissing
    };
  }

  areDetailsMissing = () => {
    if (this.props.settingsGroup === 'businessDetails') {
      return shouldInfluencerFillBusinessDetails(this.props.organizationSettings);
    }

    return shouldInfluencerFillBankDetails(this.props.organizationSettings);
  };

  getOrganizationDetailsState(nextProps: Props): OrganizationSettingsForm {
    const { organizationSettings } = nextProps;
    const { billingAddress, businessDetails, bankDetails, proofDocuments } = organizationSettings;
    const entrepreneur = this.handleBooleanValue(businessDetails?.entrepreneur);

    return {
      bankDetails: {
        accountHolder: bankDetails?.accountHolder ?? '',
        accountNumber: bankDetails?.accountNumber ?? '',
        swift: bankDetails?.swift ?? ''
      },
      billingAddress: {
        legalName: billingAddress?.legalName ?? '',
        addressLine1: billingAddress?.addressLine1 ?? '',
        city: billingAddress?.city ?? '',
        state: billingAddress?.state ?? '',
        postalCode: billingAddress?.postalCode ?? '',
        country: billingAddress?.country ?? '',
        phone: billingAddress?.phone ?? ''
      },
      businessDetails: {
        companyName: businessDetails?.companyName ?? '',
        registeredBusiness: true,
        taxId: businessDetails?.taxId ?? '',
        website: businessDetails?.website ?? '',
        taxIdCategory: businessDetails?.taxIdCategory || null,
        entrepreneur
      },
      proofDocuments
    };
  }

  getCountryType(): string | null {
    const { organizationSettings } = this.props;
    const country = this.state.country ?? organizationSettings?.billingAddress?.country;

    return getPaymentDetailsExplanationType(country);
  }

  handleBooleanValue(value) {
    return value || (value === false ? false : '');
  }

  handleLocationInput = (event, location) => {
    const { organizationDetails } = this.state;
    const details = organizationDetails.billingAddress;

    const updatedLocation = {
      city: location.city || details.city || '',
      state: location.state || details.state || '',
      country: location.country || details.country || ''
    };

    this.setState({
      isModified: true,
      country: location.country,
      organizationDetails: {
        ...organizationDetails,
        billingAddress: {
          ...(organizationDetails.billingAddress || {}),
          ...updatedLocation
        }
      }
    });
  };

  handleTextInput = (event: React.ChangeEvent<HTMLInputElement>, type: string, shouldBeInUpperCase: boolean = false) => {
    const input = event.target;
    const value = shouldBeInUpperCase ? (input.value || '').toUpperCase() : input.value;

    if (!type) {
      this.setState(state => ({
        isModified: true,
        organizationDetails: {
          ...(state.organizationDetails || {}),
          [input.name]: value
        }
      }));
    } else {
      this.setState(state => ({
        isModified: true,
        organizationDetails: {
          ...(state.organizationDetails || {}),
          [type]: {
            ...(state.organizationDetails[type] || {}),
            [input.name]: value
          }
        }
      }));
    }
  };

  handleModeSwitch = (settingsGroup: string | null) => {
    if (settingsGroup === null) {
      this.setState({ editMode: false });
    } else {
      this.handleSwitchToEditMode();
    }
  };

  handleSwitchToEditMode = () => this.setState({ editMode: true });

  preparePayload = () => {
    const { organizationDetails } = this.state;

    const trimmedPayload = prepareSettingsPayload(organizationDetails);
    trimmedPayload.businessDetails.website = addProtocol(trimmedPayload.businessDetails?.website);

    return trimmedPayload;
  };

  // save on modal button save and validate
  handleSave = () => {
    const { areDetailsMissing, isModified } = this.state;
    const { settingsId, settingsGroup, subcampaignId, onSave, dispatchSaveOrganizationSettings } = this.props;
    const trimmedPayload = this.preparePayload();

    if (!isModified && !areDetailsMissing) {
      this.setState({
        editMode: false
      });

      return;
    }

    // @ts-ignore because of trimmedPayload possible type
    dispatchSaveOrganizationSettings(settingsId, trimmedPayload, {
      subcampaignId,
      // Specify updateType for sending System Message
      updateType: snakeCase(settingsGroup) as OrganizationSettingsUpdateType
    })
      .then(() => this.setState({
        editMode: false,
        areDetailsMissing: false
      }))
      .catch(() => {})
      .then(() => {
        if (onSave) {
          onSave();
        }
      });
  };

  handleSkip = (e) => {
    const { onContinue } = this.props;

    if (onContinue) {
      onContinue(e, true);
    }
  };

  renderLink() {
    const { organizationSettings, settingsGroup, onContinue } = this.props;
    const { editMode } = this.state;

    if (!editMode) {
      return (
        <div className="shipping-step-settings-form__link-wrapper">
          <ActionLink
            color="secondary"
            size="small"
            onClick={this.handleSwitchToEditMode}
          >
            {__t(`${msgKey}.link_edit_${snakeCase(settingsGroup)}`)}
          </ActionLink>
        </div>
      );
    }

    return (
      <div className="shipping-step-settings-form__link-wrapper">
        {
          (shouldInfluencerFillBusinessDetails(organizationSettings) && settingsGroup === 'businessDetails' && onContinue) &&
            <ActionLink
              color="secondary"
              size="small"
              onClick={this.handleSkip}
            >
              {__t(`${msgKey}.link_skip_business_details`)}
            </ActionLink>
        }
      </div>
    );
  }

  renderActions() {
    const { submitButtonColor, onCancel, onContinue } = this.props;
    const { editMode } = this.state;

    return (
      <ModalFrameFooter>
        <ActionButton
          color="tertiary"
          onClick={onCancel}
        >
          {__t(`${msgKey}.button_cancel`)}
        </ActionButton>
        {
          editMode ? (
            <ActionButton
              type="submit"
              color={submitButtonColor}
            >
              {__t(`${msgKey}.button_save`)}
            </ActionButton>
          ) :
            <ActionButton
              onClick={onContinue || onCancel}
              color={submitButtonColor}
            >
              {__t(`${msgKey}.button_ok`)}
            </ActionButton>
        }
      </ModalFrameFooter>
    );
  }

  renderDisclaimer() {
    const { disclaimer, editMode } = this.state;

    if (editMode) {
      return (
        <FormField
          label={__t(`${msgKey}.disclaimer_title`)}
          labelType="highlighted"
          direction="column"
          fontSize="small"
          showRequiredMark
          validators={[
            isRequired(disclaimer)
          ]}
        >
          <InputCheckbox
            id="disclaimer"
            type="text"
            name="disclaimer"
            checked={disclaimer}
            labelClass="shipping-step-settings-form__disclaimer-checkbox"
            label={__t(`${msgKey}.disclaimer_note`)}
            onChange={() => this.setState(state => ({ disclaimer: !state.disclaimer }))}
          />
        </FormField>
      );
    }

    return (
      <div>
        <div className="shipping-step-settings-form__disclaimer-title">
          {__t(`${msgKey}.disclaimer_title`)}
        </div>
        <div className="shipping-step-settings-form__disclaimer-note">
          {__t(`${msgKey}.disclaimer_note`)}
        </div>
      </div>
    );
  }
  renderBankDetailsForm() {
    const { organizationDetails, editMode } = this.state;

    if (!editMode) {
      return (
        <BankDetailsSettings
          values={this.state.organizationDetails}
        >
          {this.renderDisclaimer()}
          {this.renderLink()}
          {this.renderActions()}
        </BankDetailsSettings>
      );
    }

    return (
      <BankDetailsSettingsForm
        direction="column"
        labelType="highlighted"
        canEdit
        values={{
          bankDetails: organizationDetails.bankDetails,
          bankDetailsProof: organizationDetails.proofDocuments?.bankDetailsProof || null
        }}
        onSubmit={this.handleSave}
        onTextInput={this.handleTextInput}
        onChange={() => null}
        onPreUpload={() => null}
      >
        {this.renderDisclaimer()}
        {this.renderLink()}
        {this.renderActions()}
      </BankDetailsSettingsForm>
    );
  }

  renderBusinessDetailsForm() {
    const { organizationDetails, editMode } = this.state;

    if (!editMode) {
      return (
        <InfluencerBusinessDetailsSettings
          values={this.state.organizationDetails}
        >
          {this.renderLink()}
          {this.renderActions()}
        </InfluencerBusinessDetailsSettings>
      );
    }

    return (
      <InfluencerBusinessDetailsSettingsForm
        direction="column"
        labelType="highlighted"
        hidePhone
        onLocationInput={this.handleLocationInput}
        canEdit
        values={{
          ...organizationDetails,
          businessDetailsProof: organizationDetails.proofDocuments?.businessDetailsProof || null
        }}
        onSubmit={this.handleSave}
        onTextInput={this.handleTextInput}
        onChange={() => null}
        onPreUpload={() => null}
      >
        {this.renderLink()}
        {this.renderActions()}
      </InfluencerBusinessDetailsSettingsForm>
    );
  }

  render(): React.ReactNode {
    const { minHeight, settingsGroup } = this.props;
    const { editMode } = this.state;
    const paymentDetailsExplanation = sanitizeGenericHtml(__t(`${msgKey}.influencers_payment_details_explanation.${this.getCountryType()}`) || '');

    return (
      <ModalFrameInner
        minHeight={minHeight}
        narrowContent
      >
        <ModalFrameMessage
          message={
              editMode ?
                __t(`${msgKey}.provide_${snakeCase(settingsGroup)}_title`) :
                __t(`${msgKey}.provide_${snakeCase(settingsGroup)}_edit_title`)
            }
        />
        {
            settingsGroup === 'businessDetails' && (
              <div
                className="shipping-step-settings-form__explanation"
                dangerouslySetInnerHTML={{ __html: paymentDetailsExplanation }}
              />
            )
          }
        <div className="shipping-step-settings-form__form">
          {
              settingsGroup === 'businessDetails' ?
                this.renderBusinessDetailsForm() :
                this.renderBankDetailsForm()
            }
        </div>
      </ModalFrameInner>
    );
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchSaveOrganizationSettings: (id: number, data: OrganizationSettingsForm, params: OrganizationSettingsParams) => dispatch(saveOrganizationSettings(id, data, params))
});

export default connect(null, mapDispatchToProps)(ShippingStepSettingsForm);
